// client/src/lib/services/auth-offline.ts
// Servicio de autenticación offline para QvaPOS
// PROPÓSITO: Permitir login sin internet tras primer acceso online
// REGLA CRÍTICA: Este módulo NUNCA valida credenciales contra el backend en modo offline.
// Solo gestiona: almacenamiento seguro del token, hash de contraseña y expiración local.

import { indexedDBService, hashPassword } from "../db/indexed-db";
import { getCurrentShop } from "../auth";

// Configuración de seguridad
const CONFIG = {
  TOKEN_EXPIRATION_DAYS: 7, // Duración máxima de sesión offline (ajustable según política de seguridad)
  MIN_PASSWORD_LENGTH: 6, // Validación básica de formato (el backend hace la validación real)
};

// Interfaz para la sesión almacenada (interna)
interface StoredSession {
  id: string;
  username: string;
  token: string;
  passwordHash: string; // SHA-256 hash, NUNCA contraseña en texto plano
  expiresAt: number; // Timestamp en milisegundos
  shopId: string;
  userId?: string; // ID real del usuario en BD
  createdAt: string;
}

// Interfaz pública para el frontend
export interface SessionData {
  token: string;
  username: string;
  shopId: string;
  expiresAt: number;
  userId?: string;
}

export const AuthService = {
  /**
   * PRIMER LOGIN ONLINE: Valida credenciales con backend y prepara para offline futuro.
   * @param username - Usuario proporcionado por el frontend
   * @param password - Contraseña en texto plano (se hashea inmediatamente, nunca se almacena)
   * @param token - JWT recibido del backend tras validación exitosa
   * @throws Error si el token es inválido o no tiene claims requeridos
   */
  async onlineLogin(
    username: string,
    password: string,
    token: string,
    userId?: string,
  ): Promise<SessionData> {
    // Validación básica de entrada (el backend ya validó, esto es solo defensa en profundidad)
    // NOTA: password puede estar vacío cuando se llama desde backend (el hash se genera en login offline)
    if (!username || !token) {
      throw new Error("CREDENCIALES_COMPLETAS_REQUERIDAS");
    }

    // {Solo validar longitud si se proporciona password. Permitir vacío para compatibilidad}
    if (
      password &&
      password.length > 0 &&
      password.length < CONFIG.MIN_PASSWORD_LENGTH
    ) {
      throw new Error("PASSWORD_DEMASIADO_CORTO");
    }
    // COMENTARIO:
    // - Permitimos password vacío para llamadas desde backend donde la contraseña ya fue validada.
    // - El hash de contraseña se generará cuando el usuario ingrese su password en login offline.
    // - Esta validación sigue protegiendo contra llamadas malformadas con username o token faltantes.

    // Decodificar payload del JWT para extraer metadata (sin verificar firma - eso lo hizo el backend)
    // Formato JWT: header.payload.signature
    try {
      const payloadBase64 = token.split(".")[1];
      console.log(
        "🔍 [AUTH-DEBUG] Token recibido (primeros 50 chars):",
        token.substring(0, 50) + "...",
      );
      console.log("🔍 [AUTH-DEBUG] Decodificando payload...");
      const payloadJson = atob(payloadBase64);
      const payload = JSON.parse(payloadJson);
      console.log(
        "🔍 [AUTH-DEBUG] Payload decodificado:",
        JSON.stringify(payload, null, 2),
      );

      // Validar que el token tiene los claims mínimos requeridos
      console.log(
        "🔍 [AUTH-DEBUG] Validando claims: exp=",
        payload.exp,
        "username=",
        payload.username,
        "shopId=",
        payload.shopId,
      );
      if (!payload.exp || !payload.username || !payload.shopId) {
        console.error(
          "❌ [AUTH-DEBUG] Claims faltantes. Payload completo:",
          payload,
        );
        throw new Error("TOKEN_INVALIDO_FALTAN_CLAIMS");
      }

      // Calcular expiración para modo offline (máximo CONFIG.TOKEN_EXPIRATION_DAYS desde ahora)
      // Usamos el menor entre: exp del token vs límite de política local
      const backendExpiresAt = payload.exp * 1000; // JWT exp está en segundos
      const policyExpiresAt =
        Date.now() + CONFIG.TOKEN_EXPIRATION_DAYS * 24 * 60 * 60 * 1000;
      const expiresAt = Math.min(backendExpiresAt, policyExpiresAt);

      // {Generar hash solo si hay password. Si está vacío, usar hash vacío}
      // {Esto permite compatibilidad con sesiones guardadas sin password}
      const passwordHash = password ? await hashPassword(password) : "";

      // Guardar sesión en IndexedDB (almacenamiento seguro, no accesible vía XSS como localStorage)
      const session: StoredSession = {
        id: crypto.randomUUID(),
        username: payload.username, // Usar username del token para consistencia
        token,
        passwordHash,
        expiresAt,
        shopId: payload.shopId,
        userId,
        createdAt: new Date().toISOString(),
      };

      await indexedDBService.saveSession(session);
      console.log(
        `✅ Sesión guardada para offline: ${username} (expira: ${new Date(expiresAt).toLocaleString()})`,
      );

      // Retornar datos públicos para el frontend
      return {
        token,
        username: payload.username,
        shopId: payload.shopId,
        expiresAt,
        userId,
      };
    } catch (err: any) {
      console.error("❌ [AUTH-DEBUG] Error en onlineLogin:", err);
      console.error("❌ [AUTH-DEBUG] Stack:", err.stack);
      console.error("❌ [AUTH-DEBUG] Token original:", token);
      throw new Error("TOKEN_JWT_INVALIDO");
    }
  },

  /**
   * LOGIN OFFLINE: Valida credenciales localmente sin contactar al backend.
   * @param username - Usuario proporcionado por el frontend
   * @param password - Contraseña en texto plano (se hashea para comparar con el hash guardado)
   * @returns Token JWT válido para usar en llamadas API
   * @throws Error si no hay sesión guardada, hash no coincide o token expiró
   */
  async offlineLogin(username: string, password: string): Promise<string> {
    // Verificar que hay una sesión guardada para este usuario
    const session = await indexedDBService.getActiveSession();

    if (!session) {
      throw new Error("NO_HAY_SESION_OFFLINE_PARA_ESTA_TIENDA");
    }

    if (session.username !== username) {
      throw new Error("USUARIO_NO_COINCIDE_CON_SESION_GUARDADA");
    }

    // Validar contraseña: hashear la ingresada y comparar con el hash guardado
    const inputHash = await hashPassword(password);
    if (inputHash !== session.passwordHash) {
      // Registrar intento fallido (opcional: para auditoría o bloqueo tras múltiples intentos)
      console.warn(
        `⚠️ Intento de login offline fallido para usuario: ${username}`,
      );
      throw new Error("CREDENCIALES_INVALIDAS");
    }

    // Validar expiración del token
    if (Date.now() > session.expiresAt) {
      console.warn(`⚠️ Sesión offline expirada para usuario: ${username}`);
      throw new Error("SESION_OFFLINE_EXPIRADA_REQUIERE_CONEXION");
    }

    console.log(`✅ Login offline exitoso: ${username}`);
    return session.token;
  },

  // ============ MÉTODOS PARA OWNER OFFLINE ============

  async ownerOnlineLogin(
    email: string,
    password: string,
    token: string,
    extra?: { planType?: string; currency?: string },
  ): Promise<{ token: string; email: string; ownerId: string; expiresAt: number; planType?: string; currency?: string }> {
    if (!email || !token) {
      throw new Error("CREDENCIALES_COMPLETAS_REQUERIDAS");
    }

    try {
      const payloadBase64 = token.split(".")[1];
      const payloadJson = atob(payloadBase64);
      const payload = JSON.parse(payloadJson);

      if (!payload.exp || !payload.ownerId || !payload.email) {
        console.error("❌ [AUTH] Claims faltantes en token de owner:", payload);
        throw new Error("TOKEN_INVALIDO_FALTAN_CLAIMS");
      }

      const backendExpiresAt = payload.exp * 1000;
      const policyExpiresAt =
        Date.now() + CONFIG.TOKEN_EXPIRATION_DAYS * 24 * 60 * 60 * 1000;
      const expiresAt = Math.min(backendExpiresAt, policyExpiresAt);

      const passwordHash = password ? await hashPassword(password) : "";

      await indexedDBService.saveOwnerSession({
        id: crypto.randomUUID(),
        email: payload.email,
        token,
        passwordHash,
        expiresAt,
        ownerId: payload.ownerId,
        planType: extra?.planType,
        currency: extra?.currency,
      });

      return { token, email: payload.email, ownerId: payload.ownerId, expiresAt, planType: extra?.planType, currency: extra?.currency };
    } catch (err: any) {
      console.error("❌ [AUTH] Error en ownerOnlineLogin:", err);
      throw new Error("TOKEN_JWT_INVALIDO");
    }
  },

  async ownerOfflineLogin(email: string, password: string): Promise<string> {
    const session = await indexedDBService.getActiveOwnerSession();

    if (!session) {
      throw new Error("NO_HAY_SESION_OFFLINE_PARA_ESTE_OWNER");
    }

    if (session.email !== email) {
      throw new Error("EMAIL_NO_COINCIDE_CON_SESION_GUARDADA");
    }

    const inputHash = await hashPassword(password);
    if (inputHash !== session.passwordHash) {
      console.warn(`⚠️ Intento de login offline fallido para owner: ${email}`);
      throw new Error("CREDENCIALES_INVALIDAS");
    }

    if (Date.now() > session.expiresAt) {
      console.warn(`⚠️ Sesión offline de owner expirada: ${email}`);
      throw new Error("SESION_OFFLINE_EXPIRADA_REQUIERE_CONEXION");
    }

    console.log(`✅ Login offline exitoso para owner: ${email}`);
    return session.token;
  },

  async getOwnerSession(): Promise<{ token: string; email: string; ownerId: string; expiresAt: number; planType?: string; currency?: string } | null> {
    const session = await indexedDBService.getActiveOwnerSession();
    if (!session || Date.now() > session.expiresAt) {
      if (session) {
        await indexedDBService.clearOwnerSession();
      }
      return null;
    }
    return {
      token: session.token,
      email: session.email,
      ownerId: session.ownerId,
      expiresAt: session.expiresAt,
      planType: session.planType,
      currency: session.currency,
    };
  },

  /**
   * Obtener sesión activa para usar en headers de API.
   * Útil para inyectar token en cada petición fetch/axios.
   * @returns SessionData con token y metadata, o null si no hay sesión válida
   */
  async getSession(): Promise<SessionData | null> {
    const session = await indexedDBService.getActiveSession();

    if (!session || Date.now() > session.expiresAt) {
      // Si expiró, limpiar automáticamente para evitar estados inconsistentes
      if (session) {
        await indexedDBService.clearSession();
        console.log("🧹 Sesión expirada eliminada automáticamente");
      }
      // {FALLBACK: Intentar sesión de owner (owner operando el POS)}
      const ownerSession = await this.getOwnerSession();
      if (ownerSession) {
        const shop = getCurrentShop();
        return {
          token: ownerSession.token,
          username: ownerSession.email,
          shopId: shop?.id || "",
          expiresAt: ownerSession.expiresAt,
        };
      }
      return null;
    }

    return {
      token: session.token,
      username: session.username,
      shopId: session.shopId,
      expiresAt: session.expiresAt,
    };
  },

  /**
   * Logout seguro: limpia sesión, cola de operaciones y caches.
   * Debe ser llamado al cerrar sesión o cambiar de tienda.
   */
  async logout(): Promise<void> {
    console.log("🔒 Iniciando logout seguro...");

    // 1. Limpiar sesión de IndexedDB (shop + owner)
    await indexedDBService.clearSession();
    await indexedDBService.clearOwnerSession();

    // 2. Limpiar cola de operaciones pendientes (no sincronizar datos de usuario anterior)
    await indexedDBService.cleanupCompletedOperations(0); // Eliminar TODO, incluso pendientes

    // 3. Notificar al Service Worker para limpiar caches
    if (navigator.serviceWorker?.controller) {
      navigator.serviceWorker.controller.postMessage({ type: "CLEAR_CACHE" });
      console.log("🗑️ Notificado al SW para limpiar caches");
    }

    // 4. (Opcional) Limpiar estado local del frontend vía evento
    window.dispatchEvent(
      new CustomEvent("qva-logout", { detail: { timestamp: Date.now() } }),
    );

    console.log("✅ Logout completado - todos los datos locales eliminados");
  },

  /**
   * Helper: Verificar si un usuario tiene credenciales guardadas para login offline.
   * Útil para mostrar UI diferenciada ("Puedes entrar sin internet" vs "Requiere conexión").
   * @param username - Usuario a verificar
   * @returns true si hay sesión válida guardada para este usuario
   */
  async canLoginOffline(username: string): Promise<boolean> {
    try {
      const session = await indexedDBService.getActiveSession();
      if (!session) return false;
      if (session.username !== username) return false;
      if (Date.now() > session.expiresAt) return false;
      return true;
    } catch {
      return false; // Ante cualquier error, asumir que no se puede login offline
    }
  },

  /**
   * Helper: Forzar renovación de sesión cuando hay conexión.
   * Útil para extender la expiración offline sin pedir contraseña de nuevo.
   * @param currentToken - Token actual para validar con backend
   * @returns Nuevo SessionData con expiración extendida, o null si falla
   */
  async refreshSession(currentToken: string): Promise<SessionData | null> {
    // Solo intentar refresh si hay conexión
    if (!navigator.onLine) {
      console.log("⚠️ Sin conexión: no se puede renovar sesión");
      return null;
    }

    try {
      // Llamar al endpoint de validación/refresco del backend
      // NOTA: Este endpoint debe existir en tu backend: POST /api/auth/refresh
      const response = await fetch("/api/auth/refresh", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${currentToken}`,
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP_${response.status}`);
      }

      const data = await response.json();
      const newToken = data.token;

      // Re-utilizar onlineLogin para guardar el nuevo token con la misma contraseña hash
      // Para esto necesitamos la contraseña... pero no la tenemos almacenada.
      // SOLUCIÓN: El backend debe devolver también un nuevo expiresAt, y actualizamos solo el token.

      // Actualización directa en IndexedDB (solo token y expiresAt)
      const session = await indexedDBService.getActiveSession();
      if (session) {
        const payloadBase64 = newToken.split(".")[1];
        const payload = JSON.parse(atob(payloadBase64));

        const backendExpiresAt = payload.exp * 1000;
        const policyExpiresAt =
          Date.now() + CONFIG.TOKEN_EXPIRATION_DAYS * 24 * 60 * 60 * 1000;
        const expiresAt = Math.min(backendExpiresAt, policyExpiresAt);

        await indexedDBService.saveSession({
          ...session,
          token: newToken,
          expiresAt,
        });

        console.log("✅ Sesión renovada exitosamente");
        return {
          token: newToken,
          username: session.username,
          shopId: session.shopId,
          expiresAt,
        };
      }
      return null;
    } catch (err) {
      console.error("❌ Error renovando sesión:", err);
      return null; // Fallar silenciosamente: el usuario puede seguir con la sesión antigua hasta que expire
    }
  },
};

// Exportar helper para inyectar token en headers (uso común en api.ts)
export function getAuthHeader(): { Authorization?: string } {
  const token = AuthService.getSession()
    .then((s) => s?.token)
    .catch(() => null);
  // NOTA: Esta función es asíncrona implícitamente. Mejor usar:
  // const session = await AuthService.getSession();
  // return { Authorization: `Bearer ${session?.token}` };
  return {}; // Placeholder - usar await en el llamado real
}

// {Exponer AuthService en window para debugging}
if (typeof window !== "undefined") {
  (window as any).__AuthService = AuthService;
}

// COMENTARIO FINAL DE SEGURIDAD:
// - Este módulo NUNCA envía credenciales al backend en modo offline.
// - La validación de contraseña es LOCAL usando hash SHA-256 (irreversible).
// - El token JWT se almacena en IndexedDB (más seguro que localStorage contra XSS).
// - La expiración se valida localmente, pero el backend siempre tiene la última palabra al sincronizar.
// - Logout limpia TODO: sesión, cola, caches. No deja residuos de datos sensibles.
